import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  DollarSign, 
  ClipboardCheck, 
  UserPlus,
  BarChart3,
  Shield,
  Settings,
  Building,
  Trophy,
  Calendar,
  Zap
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const QUICK_ACCESS_ITEMS = [
  {
    id: 'operations',
    label: 'Operations Control',
    icon: LayoutDashboard,
    path: '/admin',
    color: 'bg-red-500',
    roles: ['ceo', 'md', 'gm']
  },
  {
    id: 'team',
    label: 'Team Dashboards',
    icon: Users,
    path: '/admin/team-dashboards',
    color: 'bg-blue-500',
    roles: ['ceo', 'md', 'gm']
  },
  {
    id: 'analytics',
    label: 'Analytics Hub',
    icon: BarChart3,
    path: '/admin/analytics',
    color: 'bg-purple-500',
    roles: ['ceo', 'md', 'gm']
  },
  {
    id: 'financials',
    label: 'Store Financials',
    icon: DollarSign,
    path: '/franchise/financials',
    color: 'bg-green-500',
    roles: ['ceo', 'md', 'gm']
  },
  {
    id: 'staff-overview',
    label: 'Staff Overview',
    icon: UserPlus,
    path: '/admin/reports/staff-overview',
    color: 'bg-pink-500',
    roles: ['ceo', 'md', 'gm', 'hr', 'hr_manager']
  },
  {
    id: 'store-health',
    label: 'Store Health',
    icon: Building,
    path: '/admin/reports/store-health',
    color: 'bg-cyan-500',
    roles: ['ceo', 'md', 'gm', 'franchise_manager', 'operations_manager']
  },
  {
    id: 'inspections',
    label: 'Inspections',
    icon: ClipboardCheck,
    path: '/inspections',
    color: 'bg-orange-500',
    roles: ['ceo', 'md', 'gm', 'operations_manager', 'franchise_manager']
  },
  {
    id: 'kpi',
    label: 'KPI Dashboard',
    icon: Trophy,
    path: '/inspections/kpi-dashboard',
    color: 'bg-yellow-500',
    roles: ['ceo', 'md', 'gm', 'operations_manager']
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: Shield,
    path: '/admin/compliance',
    color: 'bg-teal-500',
    roles: ['ceo', 'md', 'gm']
  },
  {
    id: 'hr',
    label: 'HR Management',
    icon: UserPlus,
    path: '/admin/hr',
    color: 'bg-indigo-500',
    roles: ['ceo', 'md', 'gm', 'hr', 'hr_manager']
  },
  {
    id: 'roster',
    label: 'Roster',
    icon: Calendar,
    path: '/admin/roster',
    color: 'bg-slate-500',
    roles: ['ceo', 'md', 'gm', 'hr', 'hr_manager']
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    path: '/admin/settings',
    color: 'bg-gray-500',
    roles: ['ceo', 'md', 'gm']
  }
];

export default function QuickAccessWidget({ showHeader = true }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Filter items based on user role
  const accessibleItems = QUICK_ACCESS_ITEMS.filter(item => 
    item.roles.includes(user?.role) || item.roles.includes('all')
  );

  // Take first 8 items for the grid
  const displayItems = accessibleItems.slice(0, 8);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full" data-testid="quick-access-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Quick Access</h3>
              <p className="text-xs text-slate-500">Jump to key areas</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <div className="grid grid-cols-4 sm:grid-cols-4 gap-2 sm:gap-3">
          {displayItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className="group flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-3 rounded-xl hover:bg-slate-50 transition-all duration-200 hover:scale-105"
                data-testid={`quick-access-${item.id}`}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${item.color} flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
                  <Icon size={18} className="text-white sm:hidden" />
                  <Icon size={22} className="text-white hidden sm:block" />
                </div>
                <span className="text-[10px] sm:text-xs font-medium text-slate-600 text-center leading-tight line-clamp-2">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
        
        {accessibleItems.length > 8 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-400 text-center">
              +{accessibleItems.length - 8} more shortcuts available
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
